import React, { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";
import { Image } from "@literati/public-ui-shared-react";

/** Ranger testimonial art (circular crop PNG from Siren); displayed at 240×240. */
const IGNITE_IMAGE = {
  src: "https://siren-images.literati.com/images/KIDS_Ranger_Testimonial.original.png",
  alt: "Child laughing while reading a Literati book",
  width: 240,
  height: 240,
};

/** Horizontal gutters come from `PageSection` `contentSpacing`; pad vertically only. */
const Wrapper = styled.div`
  width: 100%;
  padding: 1.5rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.25rem;
  }
`;

const ColImage = styled.div`
  flex: 0 0 240px;
  width: 240px;
  height: 240px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  img {
    width: 240px;
    height: 240px;
    object-fit: contain;
  }
`;

const ColContent = styled.div`
  flex: 1.5;
  max-width: 478px;
  padding: 0 2.4375rem;
  display: flex;
  flex-direction: column;

  @media (max-width: 1024px) {
    max-width: 100%;
    padding: 0 1.5rem;
    align-items: center;
  }
`;

const Heading = styled.h2`
  color: ${({ theme }) => theme.colors["ink-black"]};
  font-family: ${({ theme }) => theme.font.fontFamily.hoeflerTitling};
  font-size: 2.25rem;
  font-weight: ${({ theme }) => theme.font.weight.roman};
  line-height: 125%;
  letter-spacing: -0.0225rem;
  margin: 0 0 1.75rem;
  text-align: left;

  @media (max-width: 1024px) {
    text-align: center;
    font-size: 2rem;
    letter-spacing: -0.02rem;
    margin-bottom: 1.5rem;
  }
`;

const Subtext = styled.p`
  color: ${({ theme }) => theme.colors["ink-black"]};
  font-family: ${({ theme }) => theme.font.fontFamily.avenir};
  font-size: 1.25rem;
  font-weight: ${({ theme }) => theme.font.weight.roman};
  line-height: 150%;
  margin: 0 0 1.75rem;
  text-align: left;

  @media (max-width: 1024px) {
    text-align: center;
    margin-bottom: 1.5rem;
    font-size: 1.125rem;
  }
`;

const BulletsContainer = styled.div`
  @media (max-width: 1024px) {
    width: fit-content;
  }
`;

const BulletRow = styled.div<{ $last?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: ${({ $last }) => ($last ? 0 : "0.75rem")};

  @media (max-width: 1024px) {
    ${({ $last }) => $last && "margin-bottom: 0 !important;"}
  }
`;

const CheckBox = styled.div`
  display: flex;
  padding: 0.35rem 0.44rem;
  justify-content: center;
  align-items: center;
  border-radius: 9999px;
  background: #d1ece9;
  flex-shrink: 0;
`;

const BulletText = styled.div`
  color: ${({ theme }) => theme.colors["ink-black"]};
  font-family: ${({ theme }) => theme.font.fontFamily.avenir};
  font-size: 1.0625rem;
  font-weight: ${({ theme }) => theme.font.weight.roman};
  line-height: 150%;
  text-align: left;
`;

const GreenLabel = styled.span<{ $breakOnMobile?: boolean }>`
  color: #18aaaa;
  font-family: ${({ theme }) => theme.font.fontFamily.avenir};
  font-size: 1.25rem;
  font-weight: ${({ theme }) => theme.font.weight.black};
  line-height: 150%;

  @media (max-width: 1024px) {
    ${({ $breakOnMobile }) =>
      $breakOnMobile &&
      `
      display: block;
    `}
  }
`;

const ColCard = styled.div`
  position: relative;
  flex: 1;
  max-width: 318px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  background: #dbf1ef;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    max-width: 100%;
    margin-top: 0;
  }
`;

const CardIcon = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  width: 48px;
  height: 51.38px;
  justify-content: center;
  align-items: center;
  border-radius: 9999px;
  background: #59cdc9;
`;

const CircleOuter = styled.div`
  width: 192px;
  height: 192px;
  border-radius: 50%;
  border: 12px solid #58cdc9;
  background: #fbfaf9;
  box-shadow: 0 0 15px 0 rgba(88, 205, 201, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  position: relative;
`;

const CircleInner = styled.div`
  display: flex;
  width: 144px;
  height: 144px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 50%;
  background: #e3f4f2;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05) inset;
`;

const IgniteNumber = styled.span`
  color: #111618;
  text-align: center;
  font-family: ${({ theme }) => theme.font.fontFamily.avenir};
  font-size: 3rem;
  font-weight: ${({ theme }) => theme.font.weight.black};
  line-height: 3rem;
  letter-spacing: -0.15rem;
`;

const IgniteUnit = styled.span`
  color: #26afaf;
  text-align: center;
  font-family: ${({ theme }) => theme.font.fontFamily.avenir};
  font-size: 0.75rem;
  font-weight: ${({ theme }) => theme.font.weight.black};
  line-height: 1rem;
  letter-spacing: 0.075rem;
  text-transform: uppercase;
  margin-top: 0.25rem;
`;

const StarsSvg = styled.svg`
  position: absolute;
  top: 1.625rem;
  right: 0.75rem;
`;

const CardDesc = styled.p`
  color: ${({ theme }) => theme.colors["ink-black"]};
  text-align: center;
  font-family: ${({ theme }) => theme.font.fontFamily.avenir};
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 125%;
  margin: 0;
`;

const SvgCheck = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="21"
    viewBox="0 0 18 21"
    fill="none"
    aria-hidden
  >
    <path
      d="M8.59679 16.1929L2.23034 12.5173L3.50909 10.2624L7.6382 12.6154L13.1517 3.06578L15.38 4.35226L8.59679 16.1929Z"
      fill="#32A8A4"
    />
  </svg>
);

const SvgTrend = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="25"
    height="28"
    viewBox="0 0 25 28"
    fill="none"
    aria-hidden
  >
    <path
      d="M15.8987 8.16663V10.1111H18.4265L13.3709 15.118L9.48204 11.2291L2.2876 18.4722L3.64871 19.8333L9.48204 14L13.3709 17.8888L19.7876 11.4722V14H21.732V8.16663H15.8987Z"
      fill="white"
    />
  </svg>
);

function animateNumber(element: HTMLElement, targetValue: number) {
  const duration = 1800;
  const startTime = performance.now();

  function update(currentTime: number) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const ease = 1 - (1 - progress) ** 3;
    const current = Math.round(ease * targetValue);
    element.innerText = String(current);
    if (progress < 1) requestAnimationFrame(update);
  }

  requestAnimationFrame(update);
}

/**
 * “Ignite a love of reading” block inside the subscriber-stats gradient card (funnel experiment lit-5).
 */
function LandingIgniteSection() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);

  const startIgniteCounter = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const el = document.getElementById("landing-ignite-mins-counter");
    if (el) animateNumber(el, 39);
  }, []);

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            startIgniteCounter();
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [startIgniteCounter]);

  return (
    <Wrapper ref={wrapperRef} aria-labelledby="landing-ignite-heading">
      <ColImage>
        <Image {...IGNITE_IMAGE} />
      </ColImage>
      <ColContent>
        <Heading id="landing-ignite-heading">Ignite a love of reading</Heading>
        <Subtext>
          Literati pairs age-right, personalized hidden gems they will love with
          the tips you need to develop their reading skills as they grow:
        </Subtext>
        <BulletsContainer>
          <BulletRow>
            <CheckBox>
              <SvgCheck />
            </CheckBox>
            <BulletText>
              <GreenLabel $breakOnMobile>Print Awareness </GreenLabel>
              Large-print books and prompts that show &quot;words have
              meaning.&quot;
            </BulletText>
          </BulletRow>
          <BulletRow>
            <CheckBox>
              <SvgCheck />
            </CheckBox>
            <BulletText>
              <GreenLabel>Phonological Awareness </GreenLabel>
              Rhymes, syllable play, and sound games.
            </BulletText>
          </BulletRow>
          <BulletRow $last>
            <CheckBox>
              <SvgCheck />
            </CheckBox>
            <BulletText>
              <GreenLabel>Vocabulary </GreenLabel>
              New words in context, plus parent prompts.
            </BulletText>
          </BulletRow>
        </BulletsContainer>
      </ColContent>
      <ColCard>
        <CardIcon>
          <SvgTrend />
        </CardIcon>
        <CircleOuter>
          <CircleInner>
            <IgniteNumber id="landing-ignite-mins-counter">0</IgniteNumber>
            <IgniteUnit>Mins</IgniteUnit>
          </CircleInner>
          <StarsSvg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            aria-hidden
          >
            <path
              d="M22 12L24 20L32 22L24 24L22 32L20 24L12 22L20 20L22 12Z"
              fill="#32A8A4"
            />
            <path
              d="M6 6L7.2 10.8L12 12L7.2 13.2L6 18L4.8 13.2L0 12L4.8 10.8L6 6Z"
              fill="#32A8A4"
            />
            <path
              d="M18 0L19.2 4.8L24 6L19.2 7.2L18 12L16.8 7.2L12 6L16.8 4.8L18 0Z"
              fill="#32A8A4"
            />
          </StarsSvg>
        </CircleOuter>
        <CardDesc>
          Literati members have reported increased daily reading times by an
          average of 39 mins.
        </CardDesc>
      </ColCard>
    </Wrapper>
  );
}

export default LandingIgniteSection;
